import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Globe, Bell, LogOut } from 'lucide-react';
import { Card, CardHeader, CardTitle, Button, Input, Select, Spinner } from '@/components/ui';
import { useAuthStore, useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import {
  SettingsTabs,
  type SettingsTab,
  CategorySection,
  EntitySection,
  AccountSection,
  UserSection,
  SecuritySection,
  DataSection,
  AuditLogSection,
} from './components';

const currencyOptions = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'CNY', label: 'CNY - Chinese Yuan' },
];

const SETTINGS_TAB_STORAGE_KEY = 'perfin_settings_tab';

// Valid tab values for validation
const VALID_TABS: SettingsTab[] = ['profile', 'security', 'preferences', 'categories', 'entities', 'accounts', 'users', 'data', 'audit'];

function getInitialTab(tabParam: SettingsTab | null): SettingsTab {
  // Priority: URL param > localStorage > default
  if (tabParam && VALID_TABS.includes(tabParam)) {
    return tabParam;
  }

  try {
    const stored = localStorage.getItem(SETTINGS_TAB_STORAGE_KEY);
    if (stored && VALID_TABS.includes(stored as SettingsTab)) {
      return stored as SettingsTab;
    }
  } catch {
    // localStorage not available
  }

  return 'profile';
}

export function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, logout, updateProfile } = useAuthStore();
  const { organization, currentUserRole } = useSettingsStore();
  // superAdmin also has admin privileges
  const isSuperAdmin = currentUserRole === 'superAdmin';
  const isAdmin = currentUserRole === 'admin' || isSuperAdmin;
  const toast = useToast();
  const { confirmWarning } = useConfirm();

  // Read tab from URL query parameter
  const tabParam = searchParams.get('tab') as SettingsTab | null;
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => getInitialTab(tabParam));

  // Sync with URL changes (URL param takes priority)
  useEffect(() => {
    if (tabParam && VALID_TABS.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [tabParam, activeTab]);

  // Persist tab selection to localStorage
  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    try {
      localStorage.setItem(SETTINGS_TAB_STORAGE_KEY, tab);
    } catch {
      // localStorage not available
    }
  };
  const [displayName, setDisplayName] = useState(profile?.displayName || user?.displayName || '');
  const [currency, setCurrency] = useState(profile?.defaultCurrency || 'USD');
  const [saving, setSaving] = useState(false);

  // Update local state when profile changes
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || '');
      setCurrency(profile.defaultCurrency || 'USD');
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile({ displayName, defaultCurrency: currency });
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    confirmWarning({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out?',
      onConfirm: async () => {
        try {
          await logout();
          toast.success('Signed out successfully');
          navigate('/login');
        } catch {
          toast.error('Failed to sign out');
        }
      },
    });
  };

  // Show loading state until critical data is available
  if (!currentUserRole || !organization) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6 max-w-2xl">
            {/* Profile Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-gray-400" />
                  <CardTitle>Profile</CardTitle>
                </div>
              </CardHeader>

              <div className="space-y-4">
                {/* Avatar */}
                <div className="flex items-center gap-4">
                  {user?.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={displayName}
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xl font-medium">
                      {(displayName || user?.email || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{displayName || 'User'}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    {organization && (
                      <p className="text-sm text-gray-400">{organization.name}</p>
                    )}
                  </div>
                </div>

                {/* Display name */}
                <Input
                  label="Display Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                />

                {/* Email (read-only) */}
                <Input
                  label="Email"
                  value={user?.email || ''}
                  disabled
                  helperText="Email cannot be changed"
                />
              </div>
            </Card>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <Button
                variant="secondary"
                onClick={handleLogout}
                leftIcon={<LogOut className="h-4 w-4" />}
              >
                Sign Out
              </Button>

              <Button onClick={handleSaveProfile} loading={saving}>
                Save Profile
              </Button>
            </div>
          </div>
        );

      case 'security':
        return <SecuritySection />;

      case 'preferences':
        return (
          <div className="space-y-6 max-w-2xl">
            {/* Preferences Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-gray-400" />
                  <CardTitle>Preferences</CardTitle>
                </div>
              </CardHeader>

              <div className="space-y-4">
                {/* Currency */}
                <Select
                  label="Default Currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  options={currencyOptions}
                  helperText="Used for transaction tracking"
                />
              </div>
            </Card>

            {/* Notifications Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-gray-400" />
                  <CardTitle>Notifications</CardTitle>
                </div>
              </CardHeader>

              <div className="text-sm text-gray-500">
                Notification preferences coming soon...
              </div>
            </Card>

            {/* Save button */}
            <div className="flex justify-end">
              <Button onClick={handleSaveProfile} loading={saving}>
                Save Preferences
              </Button>
            </div>
          </div>
        );

      case 'categories':
        return <CategorySection />;

      case 'entities':
        return <EntitySection />;

      case 'accounts':
        return <AccountSection />;

      case 'users':
        return isAdmin ? <UserSection /> : null;

      case 'data':
        return isAdmin ? <DataSection /> : null;

      case 'audit':
        return isSuperAdmin ? <AuditLogSection /> : null;

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account and preferences</p>
      </div>

      {/* Tabs */}
      <SettingsTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
      />

      {/* Tab Content */}
      <div className="py-4">{renderTabContent()}</div>
    </div>
  );
}
